import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { useAuth } from '@/contexts/AuthContext';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { ensurePushSubscription, isPushSupported } from '@/lib/push';

const NotificationsContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const native = Capacitor.isNativePlatform();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  };

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!user) { setNotifications([]); setUnreadCount(0); return; }
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user]);

  // If the user already granted permission in a previous session, silently
  // (re)register the push subscription. New permissions require a user gesture
  // via requestPushPermission() below.
  useEffect(() => {
    if (native) {
      LocalNotifications.checkPermissions().then(p => setPushPermission(p.display === 'granted' ? 'granted' : p.display === 'denied' ? 'denied' : 'default'));
      return;
    }
    if (!user || !isPushSupported()) return;
    if (Notification.permission === 'granted') {
      ensurePushSubscription({ requestPermission: false }).catch(() => {});
    }
  }, [native, user]);

  const requestPushPermission = useCallback(async () => {
    if (native) {
      const permission = await LocalNotifications.requestPermissions();
      const granted = permission.display === 'granted';
      setPushPermission(granted ? 'granted' : 'denied');
      window.dispatchEvent(new Event('mvp-reminders-changed'));
      return { ok: granted, reason: granted ? undefined : 'denied' };
    }
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
    const result = await ensurePushSubscription({ requestPermission: true });
    setPushPermission(Notification.permission);
    return result;
  }, [native]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
    pushPermission,
    pushSupported: native || isPushSupported(),
    requestPushPermission,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsContext;
